import { redirect, RouteObject, Navigate } from 'react-router-dom'
import { lazy } from 'react'
import { App } from './App'
import { AppIndex } from './apps'
import APIKeysAndSecretsPage from './pages/ApiKeysAndSecrets/ApiKeysAndSecrets.page'
import { BillingPage } from './pages/Billing/Billing.page'
import { CustomAppListPage } from './pages/CustomApps/CustomAppList.page'
import { CustomAppsHelloPage } from './pages/CustomApps/CustomAppsHello.page'
import CustomAppsCreatePage from './pages/CustomApps/CustomAppsCreate.page'
import { DataRetentionPage } from './pages/DataRetention/DataRetention.page'
import { HomePage } from './pages/Home.page'
import { DemoPage } from './pages/Landing/DemoPage'
import { JobsPage } from './pages/Jobs/Jobs.page'
import { MappingListPage } from './pages/Mapping/MappingList.page'
import { ProjectsRequestPage } from './pages/Projects/ProjectsRequest.page'
import { CustomDashboardPage } from './pages/CustomApps/CustomDashboard.page'
import { MappingListDetailPage } from './pages/Mapping/MappingListDetail.page'
import { LogsPage } from './pages/EventLogs/Logs.page'
import { ManageUsersPage } from './pages/ManageUsers/ManageUsers.page'
import { AgentsPage } from './pages/Agents/Agents.page'
import { useCollection } from './resources/hooks/useCollection'
import { useIdentity } from './hooks/useIdentity'
import useResource from './resources/hooks/useResource'
import { CustomErrorComponent } from './components/Shared/CustomErrorComponent'
import { AutoBuildFlow } from './pages/CustomApps/AutoBuild/AutoBuildFlow'
import { FlatfileOSFlow } from './pages/CustomApps/FlatfileOS/FlatfileOSFlow'
import { LogoutPage } from './pages/Logout.page'
import { PersonalAccessTokensPage } from './pages/PersonalAccessTokens/PersonalAccessTokens.page'
import { FirstLoginPage } from './pages/FirstLogin/FirstLogin.page'

const PortalHelloPage = lazy(() => import('./pages/Portal/PortalHello.page'))
const ProjectsHelloPage = lazy(() => import('./pages/Projects/ProjectsHello.page'))
const MappingHelloPage = lazy(() => import('./pages/Mapping/MappingHello.page'))
const AccountPage = lazy(() => import('./pages/Account.page'))

// Component to handle redirect to the default app
const RedirectToApp = () => {
  const apps = useCollection('APPS')
  const { identity } = useIdentity()
  const defaultAppId = identity.account?.defaultAppId || apps[0]?.id
  const defaultApp = useResource('APPS', defaultAppId)
  const appPartialUrl = defaultApp?.type === 'CUSTOM' ? `custom/${defaultAppId}` : `${defaultApp?.type?.toLowerCase()}`
  return apps.length > 0 && identity.environments.length > 0 && defaultApp?.activatedAt ? (
    <Navigate to={`/apps/${appPartialUrl}/list/recent/`} replace />
  ) : (
    <HomePage />
  )
}

/**
 * While routes can be managed as <Route /> components too, there's a slight
 * performance advantage to not initializing them in a function each time.
 *
 * We're taking the new createBrowserRouter() approach in react-router and
 * this is the registry of all routes.
 */
export const routes: RouteObject[] = [
  {
    path: '/',
    element: <App />,
    children: [
      {
        index: true,
        element: <RedirectToApp />,
      },
      {
        path: '/first-login',
        element: <FirstLoginPage />,
      },
      {
        path: '/home/*',
        element: <HomePage />,
      },
      {
        path: '/demo/*',
        element: <DemoPage />,
      },
      {
        path: '/account/*',
        element: <AccountPage />,
      },
      {
        path: '/apps/custom',
        children: [
          {
            path: 'start',
            element: <CustomAppsHelloPage />,
          },
        ],
      },
      {
        path: '/apps/custom/:id/start',
        element: <CustomAppsCreatePage />,
      },
      {
        path: '/apps/mapping/start',
        element: <MappingHelloPage />,
      },
      {
        path: '/apps/mapping/list/:filter',
        element: <MappingListPage />,
      },
      {
        path: '/apps/mapping/detail/:id',
        element: <MappingListDetailPage />,
      },
      {
        path: '/keys-and-secrets',
        element: <APIKeysAndSecretsPage />,
      },
      {
        path: '/logs',
        element: <LogsPage />,
      },
      {
        path: '/agents',
        element: <AgentsPage />,
      },
      {
        path: '/apps/portal/start',
        element: <PortalHelloPage />,
      },
      {
        path: '/apps/projects/start',
        element: <ProjectsHelloPage />,
      },
      {
        path: '/apps/projects/learn',
        element: <ProjectsRequestPage />,
      },
      {
        path: '/apps/:namespace',
        element: <AppIndex />,
        children: [
          {
            index: true,
            loader: async (url) => {
              // todo: if app has been activated, redirect to the app
              // otherwise, redirect to the app activation page
              return redirect(`/apps/${url.params.namespace}/start/`)
            },
          },
          {
            path: 'list',
            element: <CustomAppListPage />,
          },
          {
            path: 'list/:filter',
            element: <CustomAppListPage />,
          },
          {
            path: 'usage',
            element: <CustomDashboardPage />,
          },
        ],
      },
      {
        path: '/apps/custom/:id',
        element: <AppIndex />,
        children: [
          {
            index: true,
            loader: async (url) => {
              // todo: if app has been activated, redirect to the app
              // otherwise, redirect to the app activation page
              return redirect(`/apps/custom/${url.params.id}/start/`)
            },
          },
          {
            path: 'list',
            element: <CustomAppListPage />,
          },
          {
            path: 'list/:filter',
            element: <CustomAppListPage />,
          },
          {
            path: 'build/:step',
            element: <AutoBuildFlow />,
          },
          {
            path: 'flatfile-os/:step',
            element: <FlatfileOSFlow />,
          },
        ],
      },
      {
        path: '/jobs',
        element: <JobsPage />,
      },
      {
        path: '/billing',
        element: <BillingPage />,
      },
      {
        path: '/data-retention',
        element: <DataRetentionPage />,
      },
      {
        path: '/users',
        element: <ManageUsersPage />,
      },
      {
        path: '/personal-access-tokens',
        element: <PersonalAccessTokensPage />,
      },
      {
        path: '/logout',
        element: <LogoutPage />,
      },
      {
        path: '*',
        element: (
          <CustomErrorComponent
            page='routes'
            message='The URL you are trying to reach does not exist. If you believe this is a mistake please reach out to support'
          />
        ),
      },
    ],
  },
]
